import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ColorTilesValuesFragmentFragmentDoc } from '@packages/gql/generated/shopping/ColorTilesValuesFragmentFragmentDoc';
import { Box } from '@packages/shared/src/components/Box/Box';
import { ColorButton } from '@packages/shared/src/components/ColorButton/ColorButton';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { Typography } from '@packages/shared/src/components/Typography/Typography';
import type { SetSelectedVariation } from '../../types/types';
import { activeStyles, defaultStyles } from './styles';
import { templateNames } from '../../hooks/useResponsiveAkamaiTemplate/config';

const IMAGETEMPLATE = templateNames.variationSelect;

/* GraphQL */ `
  fragment ColorTilesValuesFragment on ProductDimension {
    values {
      displayText
      image
      isSelected
      rgb
      sku
    }
  }
`;

export type ColorTilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof ColorTilesValuesFragmentFragmentDoc>;
  selectedVariation: string | null;
  onChange: SetSelectedVariation;
};

/**
 * ColorTilesDimension to choose a product color on Detailview
 * */
export const ColorTilesDimension = ({
  dimensionData,
  onChange,
  selectedVariation,
}: ColorTilesDimensionProps) => {
  const dimension = getFragmentData(ColorTilesValuesFragmentFragmentDoc, dimensionData);
  const selectedColor = dimension.values.find((value) => value.isSelected);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {selectedColor ? selectedColor.displayText : ''}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} sx={{ mt: 0.75 }}>
        {dimension.values.map(({ sku, image, rgb, displayText, isSelected }) => {
          const isActive = isSelected || false;
          return (
            <ColorButton
              onClick={() => onChange(sku)}
              key={`colorBtn${sku}`}
              minimal={false}
              image={image ? `${image}?${IMAGETEMPLATE}` : undefined}
              fitBackgroundImage
              color={rgb ? `#${rgb}` : ''}
              name={displayText || ''}
              active={isActive}
              isSelected={selectedVariation === sku}
              sx={isActive ? activeStyles : defaultStyles}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
